import type { TestType } from 'src/api/types'
import { useAdminAccess } from 'src/helpers/hooks'
import { useTranslatable } from 'src/hooks/locale/utils'
import Checkbox from 'src/views/components/forms/formik/Checkbox'
import { FormError } from 'src/views/components/forms/formik/FormError'
import { FormSubmit } from 'src/views/components/forms/formik/FormSubmit'
import SelectInput from 'src/views/components/forms/formik/SelectInput'
import TextInput from 'src/views/components/forms/formik/TextInput'
import FormikTinyMce from 'src/views/components/forms/FormikTinyMce'
import Loader from 'src/views/components/Loader'
import { GRID_THREE, GRID_TWO } from './constants'
import { FormSection } from './FormSection'
import { useProgramFormOptions } from './useProgramFormOptions'

export function FormContent(props: {
  readonly programTestTypes: readonly TestType[] | undefined
  readonly isProgramLoading: boolean
}): JSX.Element {
  const { programTestTypes, isProgramLoading } = props
  const t = useTranslatable()
  const {
    facultyOptions,
    facultySchoolOptions,
    degreeOptions,
    statusOptions,
    testTypeOptions,
    learningLangOptions,
    isLoading,
    isLoadingFacultySchools,
  } = useProgramFormOptions({ programTestTypes, isProgramLoading })
  const canManagePrograms = useAdminAccess().canManage('programs')

  return (
    <fieldset disabled={isLoading || !canManagePrograms} className="min-w-0">
      {isLoading ? (
        <div className="mb-6 flex justify-center py-2">
          <Loader className="flex" />
        </div>
      ) : null}
      <FormError />
      <FormSection title={t('program:program_name')}>
        <div className={GRID_TWO}>
          <TextInput name="name" label={t('common:name')} type="text" />
          <TextInput name="nameEn" label={t('common:name_en')} type="text" />
          <FormikTinyMce name="fullname" label={t('program:fullname')} />
          <FormikTinyMce name="fullnameEn" label={t('program:fullname_en')} />
          <FormikTinyMce name="minorFullname" label={t('program:minor_fullname')} />
          <FormikTinyMce name="minorFullnameEn" label={t('program:minor_fullname_en')} />
          <TextInput name="direction" label={t('program:direction')} type="text" />
          <TextInput name="directionEn" label={t('program:direction_en')} type="text" />
          <TextInput name="specialty" label={t('program:specialty')} type="text" />
          <TextInput name="specialtyEn" label={t('program:specialty_en')} type="text" />
        </div>
      </FormSection>

      <FormSection title={t('program:faculty_program')}>
        <div className={GRID_TWO}>
          <SelectInput
            name="faculty"
            label={t('faculty:faculty_name')}
            options={facultyOptions}
            placeholder={t('faculty:faculty_name')}
          />
          <SelectInput
            name="facultySchoolId"
            label={t('faculty:faculty_school')}
            options={facultySchoolOptions}
            placeholder={t('faculty:faculty_school')}
            isLoading={isLoadingFacultySchools}
          />
        </div>
      </FormSection>

      <FormSection title={t('program:program_code')}>
        <div className={GRID_TWO}>
          <TextInput name="prefix" label={t('common:prefix')} type="text" />
          <TextInput name="code" label={t('common:code')} type="text" />
        </div>
      </FormSection>

      <FormSection title={t('common:detailed_information')}>
        <div className={GRID_TWO}>
          <SelectInput
            name="degree"
            label={t('person:degree')}
            options={degreeOptions}
            placeholder={t('person:degree')}
          />
          <SelectInput
            name="status"
            label={t('common:status')}
            options={statusOptions}
            placeholder={t('common:status')}
          />
        </div>
      </FormSection>

      <FormSection title={`${t('program:major')} / ${t('program:minor')}`} className="mb-6">
        <div className="flex flex-wrap gap-6 rounded-md border border-borderGrey/80 bg-[rgba(0,0,0,0.02)] px-4 py-3 dark:bg-[rgba(255,255,255,0.03)]">
          <Checkbox name="hasMajor" label={t('program:major')} />
          <Checkbox name="hasMinor" label={t('program:minor')} />
        </div>
      </FormSection>

      <FormSection title={t('common:registration')}>
        <div className={GRID_TWO}>
          <div className="flex flex-col gap-3">
            <span className="text-sm font-medium text-primaryTextColor">{t('program:registration_program')}</span>
            <Checkbox name="allowInRegistration" label={t('program:allowed')} />
          </div>
          <SelectInput
            name="testType"
            label={t('program:test_type')}
            options={testTypeOptions}
            placeholder={t('program:test_type')}
          />
        </div>
        <div className="mt-4 flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium text-primaryTextColor">{t('program:allow_in_mobility')}</span>
            <Checkbox name="allowInMobility" label={t('program:allowed')} />
          </div>
          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium text-primaryTextColor">{t('program:fails_for_expel')}</span>
            <Checkbox name="failsForExpel" label={t('discussion:switched_on')} />
          </div>
        </div>
      </FormSection>

      <FormSection title={t('program:registration_requirements')}>
        <FormikTinyMce name="registrationRequirements" label={t('program:registration_requirements')} />
      </FormSection>

      <FormSection title={t('program:free_credits')}>
        <div className="mb-4 flex flex-wrap gap-6">
          <Checkbox name="hasFreeCredits" label={t('discussion:switched_on')} />
          <Checkbox name="languageRestrictionEnabled" label={t('program:language_restriction')} />
          <Checkbox name="showFreeCreditCourses" label={t('program:show_free_credit_courses')} />
        </div>
        <p className="mb-3 text-sm text-primaryTextColor/80">{t('common:credits')}</p>
        <div className={GRID_THREE}>
          <TextInput name="genCredits" label={t('program:gen_credits')} type="number" />
          <TextInput name="baseCredits" label={t('program:base_credits')} type="number" />
          <TextInput name="otherCredits" label={t('program:other_credits')} type="number" />
        </div>
        <p className="mb-3 mt-4 text-sm text-primaryTextColor/80">{t('program:minor_programme')}</p>
        <div className={GRID_THREE}>
          <TextInput name="minorGenCredits" label={t('program:minor_gen_credits')} type="number" />
          <TextInput name="minorBaseCredits" label={t('program:minor_base_credits')} type="number" />
          <TextInput name="minorOtherCredits" label={t('program:minor_other_credits')} type="number" />
        </div>
      </FormSection>

      <FormSection title={t('program:program_description_for_majors')}>
        <div className={GRID_TWO}>
          <FormikTinyMce name="description" label={t('portfolio:description')} />
          <FormikTinyMce name="descriptionEn" label={t('portfolio:description_en')} />
        </div>
      </FormSection>

      <FormSection title={t('program:program_description_for_minors')}>
        <div className={GRID_TWO}>
          <FormikTinyMce name="minorDescription" label={t('program:descritpion_for_minors')} />
          <FormikTinyMce name="minorDescriptionEn" label={t('program:descritpion_for_minors_en')} />
        </div>
      </FormSection>

      <FormSection title={t('program:registration_requirements_for_majors')}>
        <div className={GRID_TWO}>
          <FormikTinyMce name="requirementsMajor" label={t('program:registration_requirements_for_majors')} />
          <FormikTinyMce name="requirementsMajorEn" label={t('program:registration_requirements_for_majors_en')} />
        </div>
      </FormSection>

      <FormSection title={t('program:registration_requirements_for_minors')}>
        <div className={GRID_TWO}>
          <FormikTinyMce name="requirementsMinor" label={t('program:registration_requirements_for_minors')} />
          <FormikTinyMce name="requirementsMinorEn" label={t('program:registration_requirements_for_minors_en')} />
        </div>
      </FormSection>

      <FormSection title={t('common:additional_information')}>
        <div className={GRID_TWO}>
          <TextInput name="qualificationMajor" label={t('program:qualification_for_majors')} type="text" />
          <TextInput name="qualificationMajorEn" label={t('program:qualification_for_majors_en')} type="text" />
        </div>
        <div className={`${GRID_TWO} mt-4`}>
          <TextInput name="qualificationFields" label={t('program:qualification_fields')} type="text" />
          <TextInput name="qualificationFieldsEn" label={t('program:qualification_fields_en')} type="text" />
        </div>
        <div className={`${GRID_TWO} mt-4`}>
          <TextInput name="price" label={t('program:programs_price')} type="number" />
          <TextInput name="qualificationCredits" label={t('program:credits_amount')} type="number" />
          <TextInput name="creditYears" label={t('program:years_amount')} type="number" />
          <SelectInput name="learningLang" label={t('program:learning_lang')} options={learningLangOptions} />
        </div>
      </FormSection>

      {canManagePrograms && (
        <FormSubmit
          label={t('common:save')}
          classNames={{
            root: 'w-full flex justify-end border-t border-borderGrey py-4 mt-4',
            button: 'h-[48px]',
          }}
        />
      )}
    </fieldset>
  )
}
